import { Button } from '../../../_core/button/button.component';
import { ACTIVE_STATUS, IInviteOverview, IMemberOverview, INVITE_STATUS } from '../../../../lib/types';
import { Dispatch, SetStateAction, useMemo, useState } from 'react';
import { activeStatus, inviteStatus } from '../../../../utils/wording.helpers';
import { StaffTable } from './staff-table.component';
import { StaffTableMultiselectActionButton } from './staff-table-multiselect-action-button/staff-table-multiselect-action-button';
import { UserIcon } from '@heroicons/react/20/solid';
import { Tooltip } from '../../../_core/tooltip.component';
import { useCompanyLocations } from '../../../domains/locations/location.service';
import { useCompanyDepartments } from '../../../domains/departments/department.service';
import { IStaffActionButtonProps, StaffActionButton } from './staff-action-button';
import { RIDialog } from '../../../_core/dialog/dialog.component';
import { EditRolesContainer } from '../../../domains/staff/edit-roles/edit-roles-form.container';
import { EditDepartmentsContainer } from '../../../domains/departments/edit/edit-departments-form.container';
import { EditLocationsContainer } from '../../../domains/locations/edit/edit-locations-form.container';
import { IAdminProps } from '../../../_core/core.types';
import { nameableListToString, useTableFilterConfig, useTableSelect } from '../../../_core/table/utils/table-utils';
import { ITableFilterConfig, ITableRowValues, TableFilterKeys } from '../../../_core/table/table.types';
import { NullableStaff } from './staff-table.types';
import { getName } from '../../../../utils/name.utils';
import { rolesToString } from './staff-table.utils';

export interface IStaffTableContainerProps extends IAdminProps {
  currUserId: string;
  invites?: IInviteOverview[];
  members?: IMemberOverview[];
  loading: boolean;
}

interface IStaffTableRowValues extends ITableRowValues {
  invite?: IInviteOverview;
  member?: IMemberOverview;
}

export const StaffTableContainer: React.FC<IStaffTableContainerProps> = ({
  currUserId,
  invites,
  isAdmin,
  loading,
  members
}) => {
  const [inviteStaff, setInviteStaff] = useState(false);
  const [showEditDepartmentsModal, setShowEditDepartmentsModal] = useState<NullableStaff>(null);
  const [showEditLocationsModal, setShowEditLocationsModal] = useState<NullableStaff>(null);
  const [showEditRolesModal, setShowEditRolesModal] = useState<NullableStaff>(null);

  const [filterConfig, filterFunc] = useTableFilterConfig({
    filters: [
      {
        title: TableFilterKeys.STATUS,
        placeholder: 'Filter by status',
        options: [ACTIVE_STATUS.ACTIVE, INVITE_STATUS.PENDING, 'EXPIRED', ACTIVE_STATUS.INACTIVE],
        value: [ACTIVE_STATUS.ACTIVE, INVITE_STATUS.PENDING],
        matcher: (curr: IMemberOverview | IInviteOverview, filterValue: string[]) => {
          if (!filterValue.length) return true;
          if (curr.deletedAt || ('user' in curr && curr.user.deletedAt))
            return filterValue.includes(ACTIVE_STATUS.INACTIVE);
          else if ('user' in curr) return filterValue.includes(ACTIVE_STATUS.ACTIVE);
          else if (curr.isExpired) return filterValue.includes('EXPIRED');
          return filterValue.includes(INVITE_STATUS.PENDING);
        }
      }
    ]
  });

  const { departments } = useCompanyDepartments();
  const { locations } = useCompanyLocations();

  // Rows shown in table
  const staff = useMemo(() => {
    if (!members || !invites) return null;

    const actionButtonProps: Omit<IStaffActionButtonProps, 'invite' | 'member'> = {
      currUserId,
      departments,
      isAdmin,
      locations,
      setShowEditDepartmentsModal,
      setShowEditLocationsModal,
      setShowEditRolesModal
    };

    const newStaff: IStaffTableRowValues[] = [];
    members?.filter(filterFunc)?.forEach((member) => {
      const newStaffValues: IStaffTableRowValues = {
        member,
        rowId: member._id,
        values: [
          {
            children:
              currUserId === member.user._id ? (
                <Tooltip text="This is you!" direction="top" distance="sm">
                  <UserIcon className="h-6 mr-2" />
                  {getName(member)}
                </Tooltip>
              ) : (
                getName(member)
              )
          },
          { children: member.user.email },
          { children: rolesToString(member.roles) },
          { children: activeStatus(!member?.deletedAt) },
          { children: nameableListToString(member.department) },
          { children: nameableListToString(member.location) },
          { children: <StaffActionButton {...actionButtonProps} member={member} /> }
        ]
      };
      newStaff.push(newStaffValues);
    });

    invites?.filter(filterFunc)?.forEach((invite) => {
      const newStaffValues: IStaffTableRowValues = {
        invite,
        rowId: invite._id,
        values: [
          { children: getName(invite) },
          { children: invite.email },
          { children: rolesToString(invite.roles) },
          { children: inviteStatus(invite) },
          { children: nameableListToString(invite.department) },
          { children: nameableListToString(invite.location) },
          { children: <StaffActionButton {...actionButtonProps} invite={invite} /> }
        ]
      };

      newStaff.push(newStaffValues);
    });

    return newStaff;
  }, [currUserId, departments, filterFunc, invites, isAdmin, locations, members]);

  return (
    <>
      <StaffTableSelectableContainer
        inviteStaff={inviteStaff}
        isAdmin={isAdmin}
        members={members}
        setInviteStaff={setInviteStaff}
        staff={staff ?? []}
        filterConfig={filterConfig}
        loading={loading}
        currUserId={currUserId}
      />
      {!!departments && (
        <RIDialog open={!!showEditDepartmentsModal} setOpen={(o) => setShowEditDepartmentsModal((s) => (o ? s : null))}>
          <EditDepartmentsContainer
            departments={departments}
            initialDepartments={showEditDepartmentsModal?.department ?? []}
            targetType={showEditDepartmentsModal && 'code' in showEditDepartmentsModal ? 'invite' : 'member'}
            targetId={showEditDepartmentsModal?._id ?? ''}
          />
        </RIDialog>
      )}
      {!!locations && (
        <RIDialog open={!!showEditLocationsModal} setOpen={(o) => setShowEditLocationsModal((s) => (o ? s : null))}>
          <EditLocationsContainer
            locations={locations}
            initialLocations={showEditLocationsModal?.location ?? []}
            targetType={showEditLocationsModal && 'code' in showEditLocationsModal ? 'invite' : 'member'}
            targetId={showEditLocationsModal?._id ?? ''}
          />
        </RIDialog>
      )}
      <RIDialog open={!!showEditRolesModal} setOpen={(o) => setShowEditRolesModal((s) => (o ? s : null))}>
        <EditRolesContainer
          invite={
            showEditRolesModal && 'code' in showEditRolesModal ? (showEditRolesModal as IInviteOverview) : undefined
          }
          member={
            showEditRolesModal && !('code' in showEditRolesModal) ? (showEditRolesModal as IMemberOverview) : undefined
          }
        />
      </RIDialog>
    </>
  );
};

const StaffTableSelectableContainer = ({
  currUserId,
  invites,
  members,
  setInviteStaff,
  staff = [],
  ...rest
}: {
  currUserId: string;
  filterConfig?: ITableFilterConfig<IInviteOverview | IMemberOverview>;
  invites?: IInviteOverview[];
  inviteStaff: boolean;
  isAdmin?: boolean;
  loading: boolean;
  members?: IMemberOverview[];
  setInviteStaff: Dispatch<SetStateAction<boolean>>;
  staff?: IStaffTableRowValues[];
}) => {
  const { clearSelected, getSelectedModels, onSelect, onSelectAll, selected } = useTableSelect({
    models: [...(members ?? []), ...(invites ?? [])],
    rows: staff
  });

  return (
    <StaffTable
      {...rest}
      selected={selected}
      setInviteStaff={setInviteStaff}
      staff={staff}
      onSelect={onSelect}
      onSelectAll={onSelectAll}
    >
      <Button onClick={() => setInviteStaff(true)} text="Invite Team Members" size="xl" />
      <StaffTableMultiselectActionButton
        currUserId={currUserId}
        getSelectedModels={getSelectedModels}
        selected={selected ?? []}
        clearSelected={clearSelected}
        staff={staff ?? []}
      />
    </StaffTable>
  );
};
